import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/common/SEOHead';
import { getIndustryPages } from '../data/industrySeoPages';

export default function IndustrySeoHub({ language = 'sv' }) {
  const isArabic = language === 'ar';
  const pages = getIndustryPages();
  const canonicalPath = isArabic ? '/ar/hemsidor-for-smaforetag' : '/hemsidor-for-smaforetag';
  const alternatePath = isArabic ? '/hemsidor-for-smaforetag' : '/ar/hemsidor-for-smaforetag';
  const contactPath = isArabic
    ? '/ar/kontakt?behov=hemsida&utm_source=seo&utm_campaign=industry-hub'
    : '/contact?behov=hemsida&utm_source=seo&utm_campaign=industry-hub';

  const title = isArabic
    ? 'مواقع للشركات الصغيرة في السويد مع دعم بالعربية | StartaWebb'
    : 'Hemsidor för småföretag och lokala tjänsteföretag | StartaWebb';
  const description = isArabic
    ? 'مواقع سويدية لشركات التنظيف والنقل والحرفيين والصالونات والمطاعم، مع تواصل بالعربية وسعر إرشادي نحو 4,900 كرون.'
    : 'Branschanpassade hemsidor för städfirmor, flyttföretag, hantverkare, salonger och restauranger. Riktpris cirka 4 900 kr för en enkel företagshemsida.';

  const collectionLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description,
    url: `https://startawebb.se${canonicalPath}`,
    inLanguage: isArabic ? 'ar-SE' : 'sv-SE',
    hasPart: pages.map((page) => ({
      '@type': 'WebPage',
      name: isArabic ? page.labelAr : page.labelSv,
      url: `https://startawebb.se${isArabic ? page.arPath : page.svPath}`,
      description: isArabic ? page.shortAr : page.shortSv,
    })),
  };

  return (
    <div lang={isArabic ? 'ar' : 'sv'} dir={isArabic ? 'rtl' : 'ltr'} className="bg-white text-slate-950">
      <SEOHead
        title={title}
        description={description}
        canonical={`https://startawebb.se${canonicalPath}`}
        keywords={isArabic
          ? 'مواقع شركات السويد, موقع شركة تنظيف, موقع شركة نقل, موقع حرفي, موقع صالون, موقع مطعم'
          : 'hemsidor småföretag, hemsida städfirma, hemsida flyttfirma, hemsida hantverkare, hemsida salong, hemsida restaurang'}
        language={isArabic ? 'ar' : 'sv'}
        locale={isArabic ? 'ar_SE' : 'sv_SE'}
        direction={isArabic ? 'rtl' : 'ltr'}
        alternateUrls={[
          { hrefLang: 'sv-SE', href: 'https://startawebb.se/hemsidor-for-smaforetag' },
          { hrefLang: 'ar-SE', href: 'https://startawebb.se/ar/hemsidor-for-smaforetag' },
          { hrefLang: 'x-default', href: 'https://startawebb.se/hemsidor-for-smaforetag' },
        ]}
        ogAlternateLocales={[isArabic ? 'sv_SE' : 'ar_SE']}
        structuredData={[collectionLd]}
      />

      <section className="relative overflow-hidden bg-gradient-to-br from-sky-50 via-white to-cyan-50">
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-sky-200/40 blur-3xl" />
        <div className="mx-auto max-w-6xl px-4 py-16 md:py-24">
          <div className="mb-7 flex justify-end">
            <Link
              to={alternatePath}
              lang={isArabic ? 'sv' : 'ar'}
              dir={isArabic ? 'ltr' : 'rtl'}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 font-bold text-slate-700 transition hover:border-sky-300 hover:text-sky-800"
            >
              {isArabic ? 'Svenska' : 'العربية'}
            </Link>
          </div>

          <div className="max-w-4xl">
            <p className="mb-5 inline-flex rounded-full border border-sky-200 bg-white px-4 py-2 text-sm font-bold text-sky-800 shadow-sm">
              {isArabic ? 'مواقع موجهة للسوق السويدي' : 'Branschanpassade hemsidor för den svenska marknaden'}
            </p>
            <h1 className="text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-950 md:text-6xl">
              {isArabic
                ? 'الموقع الجيد يشرح النشاط بالطريقة التي يحتاجها عميل ذلك القطاع.'
                : 'En bra hemsida förklarar verksamheten på det sätt just den branschens kund behöver.'}
            </h1>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-600 md:text-xl">
              {isArabic
                ? 'لا ننسخ نفس الصفحة لكل الشركات. شركة التنظيف تحتاج RUT وطلب سعر، والحرفي يحتاج صور مشاريع، والصالون يحتاج الأسعار والحجز، والمطعم يحتاج قائمة واضحة وساعات صحيحة.'
                : 'Samma malltext fungerar inte för alla. Städfirman behöver RUT och offertflöde, hantverkaren behöver projektbilder, salongen behöver priser och bokning och restaurangen behöver meny och aktuella öppettider.'}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to={contactPath} className="inline-flex items-center justify-center rounded-xl bg-sky-700 px-7 py-4 font-bold text-white shadow-lg shadow-sky-700/20 transition hover:bg-sky-800">
                {isArabic ? 'اشرح نشاط شركتك' : 'Beskriv företagets behov'}
              </Link>
              <Link to={isArabic ? '/ar/priser' : '/#pris'} className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-7 py-4 font-semibold text-slate-800 transition hover:border-sky-300 hover:text-sky-800">
                {isArabic ? 'اقرأ عن السعر' : 'Se riktpriset'}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-12 max-w-3xl">
            <p className="mb-3 font-semibold text-sky-700">{isArabic ? 'اختر النشاط' : 'Välj bransch'}</p>
            <h2 className="text-3xl font-bold tracking-tight text-slate-950 md:text-4xl">
              {isArabic ? 'صفحات تركز على ما يحول الزائر إلى تواصل' : 'Sidor som fokuserar på det som leder besökaren till kontakt'}
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {pages.map((page, index) => (
              <Link
                key={page.id}
                to={isArabic ? page.arPath : page.svPath}
                className="group flex min-h-[250px] flex-col rounded-3xl border border-slate-200 bg-slate-50 p-7 transition hover:-translate-y-1 hover:border-sky-200 hover:bg-white hover:shadow-xl"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-100 font-extrabold text-sky-800">0{index + 1}</span>
                <h3 className="mt-6 text-2xl font-bold text-slate-950 group-hover:text-sky-800">
                  {isArabic ? page.labelAr : page.labelSv}
                </h3>
                <p className="mt-3 flex-1 leading-8 text-slate-600">{isArabic ? page.shortAr : page.shortSv}</p>
                <span className="mt-6 font-bold text-sky-700">
                  {isArabic ? 'افتح صفحة النشاط ←' : 'Öppna branschsidan →'}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-slate-100 bg-slate-50 py-20">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 lg:grid-cols-3">
          {[
            isArabic
              ? ['محتوى حقيقي', 'الخدمات والأسعار والمناطق والمواد تأتي من طريقة عمل الشركة، لا من نص عام.']
              : ['Verkligt innehåll', 'Tjänster, priser, områden och material utgår från hur företaget faktiskt arbetar.'],
            isArabic
              ? ['خطوة تواصل واضحة', 'كل صفحة تحدد هل الأفضل اتصال أو عرض سعر أو حجز أو رابط إلى نظام موجود.']
              : ['Tydligt nästa steg', 'Varje sida avgör om kontakt, offert, bokning eller länk till befintligt system passar bäst.'],
            isArabic
              ? ['لا وعود كاذبة', 'لا نضمن مركزًا في Google أو عددًا من العملاء؛ نبني صفحة أسرع وأوضح وقابلة للقياس.']
              : ['Inga tomma löften', 'Ingen garanterad Google-placering eller kundmängd; fokus ligger på en snabb, tydlig och mätbar sida.'],
          ].map(([heading, text]) => (
            <article key={heading} className="rounded-3xl border border-slate-200 bg-white p-7">
              <h3 className="text-xl font-bold text-slate-950">{heading}</h3>
              <p className="mt-3 leading-8 text-slate-600">{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-5xl px-4 text-center">
          <p className="font-semibold text-sky-700">{isArabic ? 'نقطة بداية بسيطة' : 'En enkel startpunkt'}</p>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-950 md:text-5xl">
            {isArabic ? 'السعر الإرشادي نحو 4,900 كرون لموقع شركة بسيط.' : 'Riktpris cirka 4 900 kr för en enkel företagshemsida.'}
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-slate-600">
            {isArabic
              ? 'ليس باقة ثابتة ولا وعدًا بنتيجة. يتم تحديد الصفحات والوظائف والمحتوى أولًا، ثم يرسل عرض مكتوب بالسعر والضريبة إن وجدت.'
              : 'Det är inte ett låst paket eller ett resultatlöfte. Sidor, funktioner och innehåll avgränsas först, därefter lämnas en skriftlig offert med pris och eventuell moms.'}
          </p>
          <Link to={contactPath} className="mt-8 inline-flex rounded-xl bg-slate-900 px-8 py-4 font-bold text-white transition hover:bg-sky-800">
            {isArabic ? 'أرسل معلومات الشركة' : 'Skicka företagets information'}
          </Link>
        </div>
      </section>
    </div>
  );
}
